import { computed, ref,toRaw, watch } from 'vue';

import { acceptHMRUpdate, defineStore } from 'pinia';
import { useNProgress } from '@vueuse/integrations/useNProgress';

import * as Sentry from "@sentry/vue";

import moment from 'moment';
import { patch } from '@/Helpers/takeLatestResponseRequest';
import { useAuthStore } from './auth';
import { useConfigStore } from './config';
import { useFormStatusStore } from './formStatus';
import { useRecentProfilesStore } from './recentProfiles';
import { useStaffStore } from '@/Stores';
import { CARE_RECIPIENT_CONTACT_TYPE, deepCopy, util } from '@/Helpers';

import { useRoute } from 'vue-router';

/*
    This store is the store dedicated for a single client and its clients. Operations such as add,
    edit, and delete contact data are supported through this store and the client data will be
    updated with the latest data from any client and/or contact changes in different components.
*/

const inactivityRefreshTime = 120000
export const useClientSingleStore = defineStore('clientSingle', () => {

    const route = useRoute()

    const authStore = useAuthStore()
    const configStore = useConfigStore()
    const staffStore = useStaffStore()
    const formStatusStore = useFormStatusStore()

    const client = ref(defaultClient())

    let cacheAdditionalFields = []

    const enableAddNewPrimaryContactMode = ref(false)

    const lastUpdated = ref(null)

    const { isLoading } = useNProgress()
    const loading = ref(isLoading)

    const maxRetryDialogId = ref(null)

    /** Whether a client is being fetched (e.g. for the search page). */
    const isFetching = ref(false)
    function setMaxRetryDialogId(id) {
        maxRetryDialogId.value = id
    }
    function removeMaxRetryDialogId() {
        maxRetryDialogId.value = null
    }
    function getMaxRetryDialogId() {
        return maxRetryDialogId.value
    }


    function clientHasPhoneNumber(targetClient) {
        if (!targetClient) {
            return false
        }

        if (!targetClient?.phone_numbers || targetClient?.phone_numbers.length == 0) {
            return false
        }

        for (const i in targetClient.phone_numbers) {
            if (!util.isEmpty(targetClient.phone_numbers[i].number)) {
                return true
            }
        }

        return false
    }

    const inactivityTimeout = ref(null);
    const resetInactivityRefreshTimer = () => {
        clearTimeout(inactivityTimeout.value)
        inactivityTimeout.value = setTimeout(refreshClientData, inactivityRefreshTime); // 2 minutes
    }

    function stopInactivityTimeout() {
        clearTimeout(inactivityTimeout.value)
    }

    function defaultClient() {
        return {
            uuid: crypto.randomUUID(),
            contacts: [],
            first_name: null,
            last_name: null,
            assigned_sales_manager: staffStore.defaultStaff(),
            address: {
                address: null,
                address_2: null,
                city: null,
                state: null,
                zip: null,
            },

            // Client Disposition
            type: 'lead',
            disposition: 'new',

            disposition_reasons: [],

            quick_recall: null,

            // Client Care Needs
            health_conditions: [],
            expert_must_have_factors: [],
            expert_wow_factors: [],
            matching_criterias: [],

            height: null,
            weight: null,
            gender: null,

            birthday: null,

            // Client Care Schedule
            charge_rates: [],

            // Client Basic Info
            service_level: null,
            service_types: [],
            number_of_adults: null,
            estimated_start_date: null,
            estimated_end_date: null,
            follow_up_date: null,
            pay_types: [],
            rate_quoted_hourly: null,
            rate_quoted_daily: null,
            marketing_source_type: null,
            marketing_source_detail: null,
            also_considering_types: [],
            also_considering_detail: null,

            total_revenue: null,

            data_completion_status: null,

            fe_generated: true,

            // Document bundles returned when requesting additional_fields=['documents']
            document_bundles: [],
        }
    }

    function getProfileDataCompletionStatus(data_completion_status) {
        if (!data_completion_status || typeof data_completion_status !== 'object') {
            return null;
        }

        const statusValues = Object.values(data_completion_status)

        if (statusValues.length === 0) {
            return null;
        }

        // Filter out not_applicable sections as they don't impact overall status
        const applicableStatuses = statusValues.filter(section => section.progress !== 'not_applicable')

        if (applicableStatuses.length === 0) {
            return null;
        }

        if (applicableStatuses.every(section => section.progress === 'great')) {
            return 'great';
        } else if (applicableStatuses.every(section => section.progress === 'good' || section.progress === 'great')) {
            return 'good';
        } else {
            return 'poor';
        }
    }

    const dispositionsRequiringReasons = computed(() => {
        return configStore.config.client_dispositions?.dispositions_requiring_reasons ?? []
    })

    function getDispositionReasonOptionsList(disposition) {
        const reasonOptions = configStore.config.client_dispositions?.disposition_reason_options ?? {}
        return reasonOptions[disposition] ?? []
    }

    function refreshClientData() {
        if (!client.value.id) {
            return
        }

        getClientById(client.value.id, cacheAdditionalFields)
            .then(response => {
                hydrate(response.data)
                resetInactivityRefreshTimer()
            })
    }

    watch([() => client.value.id], (newValue, oldValue) => {
        if (typeof route.name === 'string' && route.name.startsWith('search.')) {
            return;
        }
        resetInactivityRefreshTimer()
    }, {immediate: true})

    watch([() => client.value.contacts], (newValue, oldValue) => {
        const clientContact = client.value.contacts.find(contact => contact.type === CARE_RECIPIENT_CONTACT_TYPE)

        if (clientContact) {
            updateClientContactInfo(clientContact)
        }
    }, {immediate: true, deep: true})

    //
    // Hydration
    //

    async function hydrate(clientData) {
        if (!clientData) {
            return
        }

        client.value = clientData
        lastUpdated.value = moment.utc()

        useRecentProfilesStore().addFromClient(clientData)
    }

    async function hydrateContact(contactData) {
        const index = client.value.contacts.findIndex(item => item.id == contactData.id)
        if (index != -1) {
            client.value.contacts[index] = contactData
        } else {
            client.value.contacts.push(contactData)
        }
        lastUpdated.value = moment.utc()

        syncFrontEndClientWithServer(client.value.id)
    }

    function syncFrontEndClientWithServer(client_id=null) {
        if (!client.value.fe_generated) {
            return
        }

        if (client_id) {
            client.value.id = client_id
        }


        getClientByUuid(client.value.uuid)
            .then(response => {
                hydrate(response.data)
            })
    }

    function updateClientContactInfo(data) {
        if (data.type == CARE_RECIPIENT_CONTACT_TYPE) {
            client.value.first_name = data.first_name
            client.value.last_name = data.last_name
            client.value.address.address = data.address
            client.value.address.address_2 = data.address_2
            client.value.address.city = data.city
            client.value.address.state = data.state
            client.value.address.zip = data.zip
            client.value.emails = data.emails
            client.value.geo = data.geo
            client.value.phone_numbers = data.phones
            client.value.birthday = data.birthday
            client.value.gender = data.gender
        } else if (!client.value.contacts.find(contact => contact.type === CARE_RECIPIENT_CONTACT_TYPE)) {
            client.value.first_name = null
            client.value.last_name = null
            client.value.address.address = null
            client.value.address.address_2 = null
            client.value.address.city = null
            client.value.address.state = null
            client.value.address.zip = null
            client.value.emails = null
            client.value.geo = null
            client.value.phone_numbers = null
            client.value.birthday = null
            client.value.gender = null
        }
    }

    function formatRequest(data, additionalFields = []) {
        if (data.assigned_sales_manager) {
            data.assigned_sales_manager_id = data.assigned_sales_manager.id
            delete data.assigned_sales_manager
        }

        if (data.marketing_source_type) {
            data.marketing_source_type = data.marketing_source_type.id
        }

        data.request_additional_fields = ['data_completion_status', ...additionalFields]

        return data
    }

    async function getClientByCoffeeUserProfileId(coffee_user_profile_id) {
        const params = {
            // additional_fields: [
            //     'notes',
            //     'disposition_history_complete',
            // ],
        }
        return await new Promise((resolve, reject) => {
            axios.get(
                `/api/v1/clients/coffee/${coffee_user_profile_id}`,
                {
                    params: params
                }
            ).then(response => {
                resolve(response)
            }).catch((err) => {
                authStore.handleError(err)
                reject({ "error": err })
            })
        });
    }

    async function getClientByUuid(client_uuid) {
        const params = {
            additional_fields: [
                'notes',
                'data_completion_status'
            ],
        }
        return await new Promise((resolve, reject) => {
            axios.get(
                `/api/v2/clients/${client_uuid}`,
                {
                    params: params
                }
            ).then(response => {
                resolve(response)
            }).catch((err) => {
                authStore.handleError(err)
                reject({ "error": err })
            })
        });
    }

    async function getClientById(client_id, additionalFields = []) {
        cacheAdditionalFields = additionalFields
        const params = {
            additional_fields: additionalFields,
        }
        return await new Promise((resolve, reject) => {
            axios.get(
                `/api/v1/clients/${client_id}`, {
                    params: params
                }
            ).then(response => {
                resolve(response)
            }).catch((err) => {
                authStore.handleError(err)
                reject({ "error": err })
            })
        });
    }

    async function updateClientUsingUUID(clientUUID, modifiedClientData, requestAdditionalFields = [], formId, referenceId) {
        loading.value = true
        if (formId) {
            formStatusStore.addLoading(formId)
        }

        return await new Promise((resolve, reject) => {
            const config = {
                headers: {
                    'X-Request-ID' : referenceId,
                }
            }
            patch(`/api/v2/clients/${clientUUID}`, formatRequest(modifiedClientData, requestAdditionalFields), config)
                .then(function (response) {
                    if (response.status === 201) {
                        const redirectUrl = response.headers.location;
                        return axios.get(redirectUrl).then(function (response) {
                            resolve(response)
                        })
                    } else {
                        resolve(response)
                    }
                }).catch(function (error) {
                    reject({ "error": error })
                    Sentry.captureException(error)
                }).finally(() => {
                    loading.value = false
                    if (formId) {
                        formStatusStore.removeLoading(formId)
                    }
                })
        });
    }

    async function updateClientDisposition(clientUuid, updatedValue) {
        loading.value = true

        return await new Promise((resolve, reject) => {
            patch(`/api/v2/clients/${clientUuid}`, {
                type: updatedValue.type,
                disposition: updatedValue.disposition,
                disposition_reasons: updatedValue.disposition_reasons ? updatedValue.disposition_reasons : [],
                request_additional_fields: ['data_completion_status'],
            })
                .then(function (response) {
                    resolve(response)
                }).catch(function (error) {
                    reject({ "error": error })
                    Sentry.captureException(error)
                }).finally(() => {
                    loading.value = false
                })
        });
    }

    async function storeContactUsingClientUuid(client_uuid, contact, formId) {
        loading.value = true
        if (formId) {
            formStatusStore.addLoading(formId)
        }

        return await new Promise((resolve, reject) => {
            axios.post('/api/v2/clients/' + client_uuid + '/contacts', contact)
                .then(function (response) {
                    if (response.status === 201) {
                        const redirectUrl = response.headers.location;
                        return axios.get(redirectUrl);
                    }
                }).then(function (response) {
                    resolve(response)
                }).catch(function (error) {
                    reject({ "error": error })
                    Sentry.captureException(error)
                }).finally(() => {
                    loading.value = false
                    if (formId) {
                        formStatusStore.removeLoading(formId)
                    }
                })
        });
    }

    async function updateContact(client, contact, formId) {
        loading.value = true
        if (formId) {
            formStatusStore.addLoading(formId)
        }

        return await new Promise((resolve, reject) => {
            patch(`/api/v1/clients/${client.id}/contacts/${contact.id}`, contact)
               .then(function (response) {
                    resolve(response)
                }).catch(function (error) {
                    reject({ "error": error })
                    Sentry.captureException(error)
                }).finally(() => {
                    loading.value = false
                    if (formId) {
                        formStatusStore.removeLoading(formId)
                    }
                })
        });
    }

    async function deleteContact(client, contact) {
        loading.value = true
        return await new Promise((resolve, reject) => {
            axios.delete(`/api/v1/clients/${client.id}/contacts/${contact.id}`, contact)
               .then(function (response) {
                    resolve(response)
                }).catch(function (error) {
                    reject({ "error": error })
                    Sentry.captureException(error)
                }).finally(() => {
                    loading.value = false
                })
        });
    }

    function applyLocalChangeToActiveClient(change) {
        const noApplyFields = ['contacts']
        for (const attr in change) {
            if (noApplyFields.indexOf(attr) == -1) {
                if (attr === 'marketing_source_type') {
                    client.value[attr] = change[attr].id;
                } else {
                    client.value[attr] = change[attr];
                }
            } else if (attr == 'contacts') {
                for (const i in change['contacts']) {
                    const index = client.value['contacts'].findIndex(item => item.uuid == change['contacts'][i].uuid)
                    if (index != -1) {
                        client.value['contacts'][index] = deepCopy(change['contacts'][i])
                    } else {
                        client.value['contacts'].push(deepCopy(change['contacts'][i]))
                    }
                }
            }

            if (attr == 'gender') {
                const clientContactIndex = client.value.contacts.findIndex(contact => contact.type === CARE_RECIPIENT_CONTACT_TYPE)
                if (clientContactIndex != -1) {
                    client.value.contacts[clientContactIndex][attr] = change[attr]
                }
            }
        }
    }

    function extractContact(proxyContact) {
        const getTypeIdOrString = (type) => {
            return typeof type === 'object' && type !== null ? type.id : type;
        };

        const obj =  {
            ...proxyContact,
            type: proxyContact.type ? getTypeIdOrString(proxyContact.type) : null,
            profession: proxyContact.profession ? getTypeIdOrString(proxyContact.profession) : null,
            state: proxyContact.state ? getTypeIdOrString(proxyContact.state) : null,
            gender: proxyContact.gender ? getTypeIdOrString(proxyContact.gender) : null,
        };

        obj['emails'] = toRaw(proxyContact.emails);
        obj['phones'] = toRaw(proxyContact.phones);

        return obj;
    }

    function extractClient(proxyClient) {
        return {
            ...proxyClient,
            contacts: proxyClient.contacts ? proxyClient.contacts.map(extractContact) : [],
        }
    }

    watch(client, function(newValue, oldValue) {
        if (newValue.id == null) {
            return;
        }

        if (typeof route.name === 'string' && route.name.startsWith('search.')) {
            return;
        }

        let title = ''

        if (newValue.first_name) {
            title += ` ${newValue.first_name}`
        }

        if (newValue.last_name) {
            title += ` ${newValue.last_name}`
        }

        if (title == '') {
            title = `Unnamed Client ${newValue.id} - LifeWorx Latte`
        } else {
            title = `${title} - Client ${newValue.id} - LifeWorx Latte`
        }

        document.title = title

    }, { immediate: true, deep: true})

    function isPrimaryContactAddNewEnable() {
        return enableAddNewPrimaryContactMode.value
    }

    function changeAddNewPrimaryContactMode(status) {
        enableAddNewPrimaryContactMode.value = status
    }

    function reset() {
        client.value = defaultClient()
        lastUpdated.value = null
        enableAddNewPrimaryContactMode.value = false
        isFetching.value = false
        stopInactivityTimeout()
        removeMaxRetryDialogId()
    }

    /**
     * Returns whether a contact has a valid primary email configured.
     * Accepts a contact object and checks the first email in the `emails` array.
     */
    function hasValidEmail(contact) {
        if (!contact) return false
        if (!Array.isArray(contact.emails) || contact.emails.length === 0) return false
        const email = String(contact.emails[0] ?? '').trim()
        if (!email) return false
        return util.isValidEmail(email)
    }

    /**
     * Fetches a client by ID and hydrates the store.
     * Manages isFetching state automatically.
     */
    async function fetchForSearch(clientId, additionalFields = []) {
        isFetching.value = true
        try {
            const response = await getClientById(clientId, additionalFields)
            hydrate(response.data)
            return response
        } finally {
            isFetching.value = false
        }
    }

    const clientAddress = computed(() => {
        const typeClientContact = client.value.contacts.find(contact => contact.type === CARE_RECIPIENT_CONTACT_TYPE)
        if (!typeClientContact) {
            return {}
        }
        return {
            address: typeClientContact.address,
            address_2: typeClientContact.address_2,
            city: typeClientContact.city,
            state: typeClientContact.state,
            zip: typeClientContact.zip,
        }
    })

    return {
        defaultClient, reset,
        hydrate, hydrateContact, getClientByCoffeeUserProfileId, getClientById,
        formatRequest, updateClientUsingUUID, updateClientDisposition,
        dispositionsRequiringReasons, getDispositionReasonOptionsList,
        getProfileDataCompletionStatus,
        updateContact, deleteContact,
        hasValidEmail,
        storeContactUsingClientUuid,
        client, lastUpdated, loading, isFetching, clientAddress,
        clientHasPhoneNumber,
        applyLocalChangeToActiveClient,
        extractContact, extractClient,
        stopInactivityTimeout, resetInactivityRefreshTimer,
        getMaxRetryDialogId, setMaxRetryDialogId, removeMaxRetryDialogId,
        isPrimaryContactAddNewEnable, changeAddNewPrimaryContactMode,
        syncFrontEndClientWithServer,
        fetchForSearch
    }

})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useClientSingleStore, import.meta.hot))
}
